import { chunk } from "./utils";
import { request } from "../helpers";

const THRESHOLD = 500;

class Coords {
  constructor(latitude = null, longitude = null, radius = null) {
    this.latitude = latitude;
    this.longitude = longitude;
    this.radius = radius;
  }

  getRadiusPoints() {
    const d = (this.radius * 1000) / 6371000;
    const lat = (Math.PI / 180) * this.latitude;
    const lon = (Math.PI / 180) * this.longitude;
    let points = [];

    for (let i = 0; i < 360; i++) {
      const degrees = (Math.PI / 180) * i;
      const y = Math.asin(
        Math.sin(lat) * Math.cos(d) +
          Math.cos(lat) * Math.sin(d) * Math.cos(degrees)
      );
      const deltaLon = Math.atan2(
        Math.sin(degrees) * Math.sin(d) * Math.cos(lat),
        Math.cos(d) - Math.sin(lat) * Math.sin(y)
      );
      const x = ((lon - deltaLon + Math.PI) % (2 * Math.PI)) - Math.PI;

      const point = {
        lat: parseFloat(y * (180 / Math.PI)),
        lng: parseFloat(x * (180 / Math.PI)),
      };

      points.push(point);
    }

    return points;
  }

  getZoomPoints() {
    if (
      this.latitude === null &&
      this.longitude === null &&
      this.radius === null
    )
      return null;

    return this.getRadiusPoints();
  }
}

class Geometry {
  constructor(element, dataSource, map, options) {
    this.element = element;
    this.options = options || {};
    this.map = map;
    this.dataSource = dataSource;

    fetchDataSource(this, dataSource);
  }

  render() {
    this.processData();
    renderGeometry(this.geometryName(), this);
  }

  center() {
    if (this.coordinates.length > 0) {
      this.map.fitBounds(this.coordinates);
    }
  }
}

class MarkerGeometry extends Geometry {
  processData() {
    const [coordinates, data] = processMarkers(this);

    this.data = data;
    this.coordinates = (this.coordinates || []).concat(coordinates);

    chunk(this.data, THRESHOLD).forEach((markers, idx) => {
      markers.forEach(marker => marker.addTo(this.map));
    });
  }

  geometryName() {
    return "MarkerGeometry";
  }

  configClusterer() {
    return L.markerClusterGroup();
  }
}

class PolygonGeometry extends Geometry {
  processData() {
    const [points, polygons] = processPolygons(this);

    this.data = polygons;
    this.coordinates = (this.coordinates || []).concat(points);

    chunk(this.data, THRESHOLD).forEach((polygons, idx) => {
      polygons.forEach((polygon) => polygon.addTo(this.map));
    });
  }

  geometryName() {
    return "PolygonGeometry";
  }
}

const Mapkick = {
  Coords: Coords,
  MarkerGeometry: MarkerGeometry,
  PolygonGeometry: PolygonGeometry,
  geometries: {},
};

if (typeof window !== "undefined" && !window.Mapkick) {
  window.Mapkick = Mapkick;
}

Mapkick.default = Mapkick;
export default Mapkick;

export const fetchDataSource = async (instance, dataSource) => {
  if (typeof dataSource === "string") {
    try {
      const response = await request.get(dataSource, {
        contentType: "application/json",
        responseKind: "json",
      });
      instance.rawData = response;
      errorCatcher(instance);
    } catch (err) {
      errorToConsole(err.message);
    }
  } else if (typeof dataSource === "function") {
    try {
      dataSource(
        (data) => {
          instance.rawData = data;
          errorCatcher(instance);
        },
        (message) => {
          errorToConsole(message);
        },
      );
    } catch (err) {
      errorToConsole(err.message);
    }
  } else {
    instance.rawData = dataSource;
    errorCatcher(instance);
  }
};

const renderGeometry = (geometryType, instance) => {
  instance.center();
};

const processMarkers = (instance) => {
  let markers = [];
  let coordinates = [];

  instance.rawData.forEach((item, idx) => {
    const { latitude, longitude, popup, icon } = item;
    const url = icon || instance.options["icon"];
    const coords = {
      lat: latitude,
      lng: longitude,
    };
    const options = {};
    if (url) {
      const baseSize = 40;
      options.icon = L.icon({
        iconUrl: url,
        iconSize: [baseSize, baseSize],
        iconAnchor: [baseSize / 2, baseSize],
        popupAnchor: [0, -baseSize]
      });
    }
    const marker = L.marker(coords, options);
    if (popup) {
      marker.bindPopup(popupContent, {minWidth: 430});
    }
    coordinates = coordinates.concat(coords);
    markers.push(marker);
  });

  return [coordinates, markers];
};

const processPolygons = (instance) => {
  let polygons = [];
  let points = [];

  instance.rawData.forEach((item) => {
    const { id, coordinates, options } = item;
    let coords = [];

    coordinates.forEach(([latitude, longitude]) => {
      const point = {
        lat: latitude,
        lng: longitude,
      }
      coords.push(point);
      points.push(point);
    });

    const polygon = L.polygon(coords, {
      ...options,
      color: '#ffffff',
      fillOpacity: 0,
      opacity: 1,
      weight: 4,
      ...options.style || {},
    });

    polygons.push(polygon);
  });

  return [points, polygons];
};

const errorCatcher = (instance) => {
  try {
    instance.render();
  } catch (err) {
    errorToConsole(err.message);
    throw err;
  }
};

const errorToConsole = (value) => {
  const message = `🚨 %c[Mapkick Error]: ${value}`;
  console.log(message, "color: #ff0000; font-weight:bold;");
};
